import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import right from '../assets/images/right.png';
import wrong from '../assets/images/wrong.png';
import pattern from '../assets/images/pattern.png';

import Progress from './Progress';
import Button from './Button';

const alphabetArr = ['A', 'B', 'C', 'D', 'E'];

const Question = ({ quiz: { id, question, explanation, choices: choiceArr }, totalQuestionNum, goNextQuestion }) => {
  // choices is an array of objects where:
  // c @string:each choice
  // a @boolean:its correctness
  // r @boolean:turns true when user selects correct answer
  // w @boolean:turns true and sets r value true(line:50) when user selects incorrect answer
  const [choices, setChoices] = useState([]);
  const [answer, setAnswer] = useState(null);
  const [isSelected, setSelected] = useState(false);
  const [showClose, setShowClose] = useState(false);

  const timeoutId1 = useRef(null);
  const timeoutId2 = useRef(null);

  const userAnswers = localStorage.getItem('user-answers') ? JSON.parse(localStorage.getItem('user-answers')) : [];

  const { t } = useTranslation();

  const handleClick = ({ c: selectedAnswer, a: isCorrect }, idx) => {
    if (localStorage.getItem(`question_${id}`)) return;

    localStorage.setItem(`question_${id}`, selectedAnswer);
    localStorage.setItem('user-answers', JSON.stringify([...userAnswers, isCorrect]));

    const newChoices = [...choices].map((choice, i) => {
      if (idx === i && choice.a) {
        return { ...choice, r: true };
      }

      if (idx === i && !choice.a) {
        return { ...choice, w: true };
      }

      return choice;
    });

    if (newChoices.some((choice) => choice.w)) {
      setChoices(newChoices.map((choice) => (choice.a ? { ...choice, r: true } : choice)));
    } else {
      setChoices(newChoices);
    }

    timeoutId1.current = setTimeout(() => {
      setSelected(true);
    }, 1200);

    timeoutId2.current = setTimeout(() => {
      setShowClose(true);
    }, 5800);
  };

  const goNext = () => {
    setChoices([...choiceArr.map((c) => ({ c: c[0], a: c[1], r: false, w: false }))]);

    setSelected(false);
    setShowClose(false);

    clearTimeout(timeoutId1.current);
    clearTimeout(timeoutId2.current);

    goNextQuestion();
  };

  useEffect(() => {
    setChoices([...choiceArr.map((c) => ({ c: c[0], a: c[1], r: false, w: false }))]);
  }, [choiceArr]);

  return (
    <>
      <img src={pattern} alt="pattern" className="lg:w-full w-[60%] absolute bottom-0 left-0" />

      <div className="md:mt-10 mt-2 relative z-20">
        <Progress id={id} totalQuestionNum={totalQuestionNum} />

        <div className="md:mx-[6rem] mx-[1.4rem] md:my-[2rem] lg:my-[5.2rem] my-[1.2rem]">
          <h3 className="sm:text-4xl lg:text-[3.2rem] text-xl md:font-bold lg:!leading-[4.2rem] text-center">
            <span dangerouslySetInnerHTML={{ __html: question }} />
          </h3>
        </div>

        <div className="bg-brandPrimary md:mx-[4rem] lg:mx-[8rem] mx-[2rem]">
          {!isSelected ? (
            choices.map((choice, idx) => {
              const value = `${id}:${choice.c}`;

              let backgroundColor = ``;
              if (choice.r) backgroundColor = `right`;
              if (choice.w) backgroundColor = `wrong`;

              return (
                <div
                  key={idx}
                  className={`_gradient-border _gradient-border${`-${backgroundColor}`} md:text-[2rem] lg:text-3xl text-xl lg:h-[140px] h-[20px] md:px-10 md:py-[3rem] lg:py-[5.8rem] py-[1.8rem] lg:mb-8 mb-4 relative flex items-center`}
                >
                  <div className="lg:text-[2.8rem] w-[90px] h-[90px] md:mr-10 ml-4 relative">
                    <span className="text-brandAccent absolute top-[50%] translate-y-[-50%] left-[50%] translate-x-[-50%]">
                      {alphabetArr[idx]}
                    </span>
                    {choice.r ? (
                      <img
                        src={right}
                        alt="right"
                        className="md:w-full w-[40px] absolute md:top-[42%] top-[46%] translate-y-[-50%] md:left-[56%] left-[54%] translate-x-[-50%]"
                      />
                    ) : (
                      ``
                    )}
                    {choice.w ? <img src={wrong} alt="wrong" className="_center-center md:w-[80%] w-[30px]" /> : ``}
                  </div>
                  <input
                    type="radio"
                    id={value}
                    value={value}
                    checked={answer === value}
                    onChange={(e) => setAnswer(e.target.value)}
                    className="w-full h-full absolute opacity-0"
                    onClick={() => {
                      handleClick(choice, idx);
                    }}
                  />
                  <label htmlFor={value} className="lg:text-[2.8rem] w-full leading-tight">
                    {choice.c}
                  </label>
                </div>
              );
            })
          ) : (
            <div className="md:text-4xl text-xl">
              <h3 className="_gradient-border-right md:px-20 px-10 md:py-6 lg:py-20 py-4 md:mb-8 mb-4 relative">
                {choices.map((choice, idx) => {
                  if (choice.a && choice.c)
                    return (
                      <div key={idx}>
                        <div
                          key={idx}
                          className="text-brandAccent lg:text-[2.8rem] absolute top-[50%] translate-y-[-50%]"
                        >
                          {alphabetArr[idx]}
                        </div>
                        <div className="lg:text-[2.8rem] leading-tight md:pl-20 pl-[3.4rem]">{choice.c}</div>
                      </div>
                    );

                  return null;
                })}
                <img
                  src={right}
                  alt="right"
                  className="md:w-[70px] w-[40px] absolute top-[46%] translate-y-[-50%] md:left-16 left-[1.8rem]"
                />
              </h3>

              <div className="_gradient-border lg:px-20 md:px-10 px-[1.2rem] md:py-6 py-4">
                <p
                  dangerouslySetInnerHTML={{ __html: explanation }}
                  className="md:text-2xl lg:text-4xl text-[1rem] leading-tight lg:my-16"
                />
                {showClose && <Button onClick={goNext}>{t('close')}</Button>}
                <div className="md:mb-10 mb-2"></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Question;
